import { mapActions, mapGetters } from 'vuex'

import { capitalize } from '@/utils/utils.js'
import debounce from 'lodash.debounce'
import { getAccessToken } from '@internetworkexpert/js-common'
import { listCollections, listLightCollections } from '@/services/collections.service'
import { listLabs } from '@/services/labs.service'
import validate from 'uuid-validate'

export const searchTags = {
  data: () => {
    return {
      tagsOptions: [],
    }
  },
  methods: {
    onSearchTags(search, loading) {
      if (search) {
        loading(true)
        this.searchTags(loading, search, this)
      }
    },
    searchTags: debounce((loading, search, vm) => {
      fetch(
        `${process.env.VUE_APP_CONTENT_API}/tags?name=${encodeURIComponent(
          search
        )}`
      ).then((res) => {
        res.json().then((json) => {
          vm.tagsOptions = json.results.reduce((acc, tag) => {
            acc.push(tag.name)
            return acc
          }, [])
        })
        loading(false)
      })
    }, 350),
  },
}

export const searchVideos = {
  methods: {
    onSearchVideos(search, loading, property, payload) {
      if (search) {
        loading(true)
        this.searchAutocompleteVideos(loading, search, this, property, payload)
      }
    },
    searchAutocompleteVideos: debounce(
      (loading, search, vm, property, payload = '') => {
        if (payload) payload = `${payload}&`
        // check if user write a valid uuid
        if (validate(search)) {
          payload += `ids=${encodeURIComponent(search)}`
        } else {
          payload += `name__icontains=${encodeURIComponent(search)}`
        }
        fetch(
          `${process.env.VUE_APP_VIDEO_API}/videos?${payload}&company=INE`
        ).then((res) => {
          res.json().then((json) => {
            let reducedResults = json.results.reduce((acc, video) => {
              acc.push({ label: video.name, value: video.id })
              return acc
            }, vm[property])
            reducedResults = reducedResults.filter(
              (video, index, self) =>
                index === self.findIndex((v) => v.value === video.value)
            )
            vm[property] = reducedResults
          })
          loading(false)
        })
      },
      350
    ),
  },
}

export const searchLPaths = {
  data: () => {
    return {
      lpathsOptions: [],
    }
  },
  methods: {
    onSearchLPaths(search, loading, filters = {}) {
      if (search) {
        loading(true)
        this.searchLPaths(loading, search, this, filters)
      }
    },
    searchLPaths: debounce((loading, search, vm, filters = {}) => {
      const extraParams = Object.entries(filters)
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `&${key}=${encodeURIComponent(value)}`)
        .join('')
      fetch(
        `${
          process.env.VUE_APP_CONTENT_API
        }/learning-paths?name__icontains=${encodeURIComponent(
          search
        )}&company=${vm.getSelectedCompany}${extraParams}`
      ).then((res) => {
        res.json().then((json) => {
          let reducedResults = json.results.reduce((acc, lp) => {
            acc.push({ label: lp.name, value: lp.id })
            return acc
          }, vm.lpathsOptions)
          reducedResults = reducedResults.filter(
            (lpath, index, self) =>
              index === self.findIndex((lp) => lp.value === lpath.value)
          )
          vm.lpathsOptions = reducedResults
        })
        loading(false)
      })
    }, 350),
  },
}

export const searchCollections = {
  data: () => {
    return {
      collectionsOptions: [],
      collectionsCache: {},
    }
  },
  methods: {
    onSearchCollections(search, loading) {
      if (search) {
        loading(true)
        this.searchCollections(loading, search, this)
      }
    },
    onSearchLightCollections(search, loading) {
      if (search) {
        loading(true)
        this.searchCollections(loading, search, this, true)
      }
    },
    searchCollections: debounce(async (loading, search, vm, lightSearch = false) => {
      let payload = {}
      if (
        search &&
        search.split(',').every((id) => {
          return validate(id.trim())
        })
      ) {
        search = search.replace(/\s/g, '')
        const arr = search.split(',')
        payload.ids = arr.length > 1 ? arr : search
      } else {
        payload.name__icontains = search ? `${search}` : null
      }
      payload.company = vm.getSelectedCompany
      let response = {}
      if (lightSearch) {
        response = await listLightCollections(payload)
      } else {
        response = await listCollections(payload)
      }
      let reducedResults = response.results.reduce((acc, lp) => {
        acc.push({ label: lp.name, value: lp.id })
        return acc
      }, [])
      reducedResults = reducedResults.filter(
        (collection, index, self) =>
          index === self.findIndex((lp) => lp.value === collection.value)
      )
      vm.collectionsOptions = reducedResults
      if (typeof vm.refreshCollectionsCacheFromOptions === 'function') {
        vm.refreshCollectionsCacheFromOptions()
      }
      loading(false)
    }, 350),
    updateCollectionsCache(values) {
      if (Array.isArray(values)) {
        if (values.length === 0) {
          this.collectionsCache = {}
          return
        }
        const nextCache = {}
        values.forEach((value) => {
          if (!value) return
          const option = this.collectionsOptions.find(
            (collection) => collection.value === value
          )
          const cachedName = this.collectionsCache[value] || null
          nextCache[value] = option ? option.label : cachedName
        })
        this.collectionsCache = nextCache
        return
      }
      if (!values) {
        this.collectionsCache = {}
        return
      }
      {
        const value = values
        const option = this.collectionsOptions.find(
          (collection) => collection.value === value
        )
        const name = option ? option.label : (this.collectionsCache[value] || null)
        this.collectionsCache = { [value]: name }
      }
    },
    seedCollectionsCacheFromItems(items) {
      if (!Array.isArray(items) || items.length === 0) {
        this.collectionsCache = {}
        return
      }
      const nextCache = {}
      items.forEach((item) => {
        if (!item) return
        const id = typeof item === 'string' ? item : (item.uuid || item.id)
        if (!id) return
        const option = this.collectionsOptions.find(
          (collection) => collection.value === id
        )
        const name = typeof item === 'string' ? null : item.name
        nextCache[id] = option ? option.label : (name || this.collectionsCache[id] || null)
      })
      this.collectionsCache = nextCache
    },
    refreshCollectionsCacheFromOptions() {
      if (!this.collectionsCache) return
      Object.keys(this.collectionsCache).forEach((id) => {
        const option = this.collectionsOptions.find(
          (collection) => collection.value === id
        )
        if (option && option.label) {
          this.$set(this.collectionsCache, id, option.label)
        }
      })
    },
    getFullCollectionsOptions() {
      const options = [...this.collectionsOptions]
      Object.entries(this.collectionsCache || {}).forEach(([id, name]) => {
        if (!options.some((option) => option.value === id)) {
          options.push({
            label: name || id,
            value: id,
          })
        }
      })
      return options
    },
    clearCollectionsOptions() {
      this.collectionsOptions = []
    },
  }
}

export const searchLabs = {
  data: () => {
    return {
      labsOptions: [],
    }
  },
  computed: {
    ...mapGetters('app', ['getSelectedCompany']),
  },
  methods: {
    onSearchLabs(search, loading) {
      if (search) {
        loading(true)
        this.searchLabs(loading, search, this)
      }
    },
    searchLabs: debounce(async (loading, search, vm) => {
      let payload = {}
      if (
        search &&
        search.split(',').every((id) => {
          return validate(id.trim())
        })
      ) {
        search = search.replace(/\s/g, '')
        const arr = search.split(',')
        payload.ids = arr.length > 1 ? arr : search
      } else {
        payload.name__icontains = search ? `${search}` : null
      }
      payload.company = vm.getSelectedCompany
      const response = await listLabs(payload)

      const selectedOptions = vm.labsOptions.filter((lab) =>
        vm.localCollection.featured_labs.includes(lab.value)
      )
      const selectedIds = selectedOptions.map((lab) => lab.value)

      const reducedResults = response.results.reduce((acc, lab) => {
        if (!selectedIds.includes(lab.id)) {
          acc.push({ label: lab.name, value: lab.id })
        }
        return acc
      }, selectedOptions)

      vm.labsOptions = reducedResults
      loading(false)
    }, 350),
  },
}

export const searchLabsTags = {
  data: () => {
    return {
      tagsOptions: [],
    }
  },
  methods: {
    onSearchTags(search, loading) {
      if (search) {
        loading(true)
        this.searchTags(loading, search, this)
      }
    },
    searchTags: debounce((loading, search, vm) => {
      fetch(
        `${process.env.VUE_APP_CONTENT_API}/lab-tags?name=${encodeURIComponent(
          search
        )}`
      ).then((res) => {
        res.json().then((json) => {
          vm.tagsOptions = json.results.reduce((acc, tag) => {
            acc.push(tag.name)
            return acc
          }, [])
        })
        loading(false)
      })
    }, 350),
  },
}

export const searchBootcamps = {
  methods: {
    onSearchBootcamps(search, loading, property, payload = '') {
      if (search) {
        loading(true)
        this.searchAutocompleteBootcamps(
          loading,
          search,
          this,
          property,
          payload
        )
      }
    },
    searchAutocompleteBootcamps: debounce(
      (loading, search, vm, property, payload = '') => {
        if (payload) payload = `${payload}&`
        // check if user write a valid uuid
        if (validate(search)) {
          payload += `ids=${encodeURIComponent(search)}`
        } else {
          payload += `name__icontains=${encodeURIComponent(search)}`
        }
        fetch(
          `${process.env.VUE_APP_CONTENT_API}/bootcamps?${payload}&company=${vm.getSelectedCompany}`,
          {
            headers: new Headers({
              Authorization: `Bearer ${getAccessToken()}`,
            }),
          }
        ).then((res) => {
          res.json().then((json) => {
            let reducedResults = json.results.reduce(
              (acc, bootcamp) => {
                acc.push({ label: bootcamp.name, value: bootcamp.id })
                return acc
              },
              [vm[property]]
            )
            reducedResults = reducedResults.filter(
              (bootcamp, index, self) =>
                index === self.findIndex((b) => b.value === bootcamp.value)
            )
            vm[property] = reducedResults
          })
          loading(false)
        })
      },
      500
    ),
  },
}

export const getBasicData = {
  computed: {
    ...mapGetters('vendors', ['getVendorsEntries']),
    ...mapGetters('instructors', ['getInstructorsEntries']),
    ...mapGetters('categories', ['getCategoriesEntries']),
    ...mapGetters('labs', ['getLabsCategoriesEntries']),
    ...mapGetters('lareas', ['getLAreasEntries']),
  },
  methods: {
    ...mapActions('vendors', ['getAllVendors']),
    ...mapActions('lareas', ['getAllLAreas']),
    ...mapActions('instructors', ['getAllInstructors']),
    ...mapActions('categories', ['getAllCategories']),
    ...mapActions('labs', ['getAllLabsCategories']),
    async getBasicData() {
      if (!Object.keys(this.getVendorsEntries).length) {
        await this.getAllVendors()
      }
      if (!Object.keys(this.getLAreasEntries).length) {
        await this.getAllLAreas()
      }
      if (!Object.keys(this.getInstructorsEntries).length) {
        await this.getAllInstructors()
      }
      if (!Object.keys(this.getCategoriesEntries).length) {
        await this.getAllCategories()
      }
    },
    async getBasicLabData() {
      if (!Object.keys(this.getVendorsEntries).length) {
        await this.getAllVendors()
      }
      if (!Object.keys(this.getLAreasEntries).length) {
        await this.getAllLAreas()
      }
      if (!Object.keys(this.getInstructorsEntries).length) {
        await this.getAllInstructors()
      }
      if (!Object.keys(this.getLabsCategoriesEntries).length) {
        await this.getAllLabsCategories()
      }
    },
  },
}

export const errorHandler = {
  methods: {
    showError(error) {
      const self = this
      function notify(errorMessage) {
        self.$vs.notify({
          title: 'Error',
          text: errorMessage,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          time: '3500',
        })
      }
      try {
        // When there is a list of key-value error
        const contentType = error && error.response && error.response.headers && error.response.headers['content-type']
        const validErrorResponse = contentType && contentType.includes('application/json')
        if (validErrorResponse && error.response && error.response.status < 500) {
          if (
            error.response &&
            error.response.data &&
            Object.keys(error.response.data).length
          ) {
            Object.entries(error.response.data).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                value.map((text) => {
                  text = text.replace('This field', `${capitalize(key)} field`)
                  notify(text)
                })
              } else {
                if (value.message) {
                  notify(value.message)
                } else {
                  notify(value)
                }
              }
            })
          } else if (error.message) {
            notify(error.message)
          } else {
            notify(error)
          }
        } else if (!error.response) {
          notify(error)
        } else {
          notify('Oops, something unexpected happened')
        }
      } catch (error) {
        notify(error)
      }
    },
  },
}

export const warningUnsaved = {
  // vars rememberToSaveFiles and rememberToSave must be declared in components
  computed: {
    unsavedForm() {
      return this.rememberToSaveFiles || this.rememberToSave
    },
  },

  beforeRouteLeave(to, from, next) {
    if (this.unsavedForm && !this.confirmLeave()) {
      next(false)
    } else {
      next()
    }
  },

  created() {
    if (!this.embedded) {
      window.addEventListener('beforeunload', this.beforeWindowUnload)
    }
  },

  beforeDestroy() {
    if (!this.embedded) {
      window.removeEventListener('beforeunload', this.beforeWindowUnload)
    }
  },

  methods: {
    confirmLeave() {
      return (
        window.confirm(
          'Do you really want to leave? you have unsaved changes!'
        ) || ''
      )
    },

    beforeWindowUnload(e) {
      if (this.unsavedForm && !this.confirmLeave()) {
        // Cancel the event
        e.preventDefault()
        // Chrome requires returnValue to be set
        e.returnValue = ''
      }
    },
  },
}

export const buildUnsavedChangesNavigationHandlers = ({
  hasUnsavedChangesMethod,
  saveForNavigationMethod,
  restoreForDiscardMethod,
} = {}) => ({
  hasUnsavedChangesForNavigation() {
    if (!hasUnsavedChangesMethod) return false
    if (typeof this[hasUnsavedChangesMethod] !== 'function') return false
    return this[hasUnsavedChangesMethod]()
  },

  async saveAndContinuePendingRoute() {
    if (!saveForNavigationMethod) return
    if (typeof this[saveForNavigationMethod] !== 'function') return

    const didSave = await Promise.resolve(this[saveForNavigationMethod]())
    if (didSave === false) {
      this.pendingLeaveRoute = null
      return
    }
    return this.continuePendingRouteNavigation()
  },

  discardAndContinuePendingRoute() {
    if (
      restoreForDiscardMethod &&
      typeof this[restoreForDiscardMethod] === 'function'
    ) {
      this[restoreForDiscardMethod]()
    }
    return this.continuePendingRouteNavigation()
  },
})

export const unsavedChangesNavigation = {
  data() {
    return {
      pendingLeaveRoute: null,
      allowRouteLeaveWithoutPrompt: false,
      showUnsavedChangesModal: false,
    }
  },

  created() {
    window.addEventListener('beforeunload', this.beforeWindowUnload)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload)
    window.removeEventListener('keydown', this.onUnsavedChangesModalKeydown)
  },

  methods: {
    hasUnsavedChangesForNavigation() {
      return false
    },

    beforeWindowUnload(e) {
      if (!this.hasUnsavedChangesForNavigation()) return
      e.preventDefault()
      e.returnValue = ''
    },

    openUnsavedChangesDialog() {
      this.showUnsavedChangesModal = true
      window.addEventListener('keydown', this.onUnsavedChangesModalKeydown)
    },

    hideUnsavedChangesModal() {
      this.showUnsavedChangesModal = false
      window.removeEventListener('keydown', this.onUnsavedChangesModalKeydown)
    },

    closeUnsavedChangesModal() {
      this.hideUnsavedChangesModal()
      this.pendingLeaveRoute = null
    },

    saveAndLeaveFromUnsavedChangesModal() {
      this.hideUnsavedChangesModal()
      if (typeof this.saveAndContinuePendingRoute === 'function') {
        this.saveAndContinuePendingRoute()
      }
    },

    discardAndLeaveFromUnsavedChangesModal() {
      this.hideUnsavedChangesModal()
      if (typeof this.discardAndContinuePendingRoute === 'function') {
        this.discardAndContinuePendingRoute()
      }
    },

    onUnsavedChangesModalKeydown(event) {
      if (!this.showUnsavedChangesModal) return
      if (event.key !== 'Escape' && event.key !== 'Esc') return

      event.preventDefault()
      this.closeUnsavedChangesModal()
    },

    continuePendingRouteNavigation() {
      if (!this.pendingLeaveRoute) return
      const nextRoute = this.pendingLeaveRoute
      this.pendingLeaveRoute = null
      this.allowRouteLeaveWithoutPrompt = true
      this.hideUnsavedChangesModal()
      return this.$nextTick().then(() =>
        Promise.resolve(this.$router.push(nextRoute)).catch(() => {
          this.allowRouteLeaveWithoutPrompt = false
          this.pendingLeaveRoute = null
        })
      )
    },

    handleUnsavedChangesBeforeRouteLeave(to, next) {
      if (this.allowRouteLeaveWithoutPrompt) {
        this.allowRouteLeaveWithoutPrompt = false
        this.hideUnsavedChangesModal()
        if (typeof this.applyRouteLeaveState === 'function') {
          this.applyRouteLeaveState(to)
        }
        next()
        return
      }

      if (this.hasUnsavedChangesForNavigation()) {
        this.pendingLeaveRoute = to
        this.openUnsavedChangesDialog()
        next(false)
        return
      }

      if (typeof this.applyRouteLeaveState === 'function') {
        this.applyRouteLeaveState(to)
      }
      next()
    },
  },
}

export const starsWidget = {
  data: () => {
    return {
      widgetActive: false,
      widgetContentId: null,
      widgetContentType: null,
      widgetPosition: { left: 0, top: 0 },
      widgetHover: false,
    }
  },
  methods: {
    showStarWidget(ref, id, type) {
      this.widgetContentId = id
      this.widgetContentType = type
      // change position of widget to child
      // take out margins
      const element = Array.isArray(this.$refs[ref])
        ? this.$refs[ref][0].$el
        : this.$refs[ref].$el
      const container = document.getElementById('content-area')
      const marginLeft = window.getComputedStyle(container).marginLeft
        ? window
            .getComputedStyle(container)
            .marginLeft.substring(
              0,
              window.getComputedStyle(container).marginLeft.length - 2
            )
        : 0
      const left = element.getBoundingClientRect().left - marginLeft + 'px'
      const top =
        element.getBoundingClientRect().bottom - 65 + window.scrollY + 'px'
      this.widgetPosition = { left: left, top: top }
      this.widgetActive = true
    },
  },
}
